/**
 * FinanceApp - components/crm/ImportInvoiceArchive.jsx
 * Import en masse de PDF de factures (export Dolibarr) en pure archive GED —
 * aucune écriture comptable créée. Rattachement automatique au client quand
 * possible (SIREN puis nom), sinon rattachement manuel via recherche.
 */
import { useState } from 'react';
import api from '../../services/api';

function fmtEUR(n) { return n == null ? '—' : `${Number(n).toLocaleString('fr-FR')} €`; }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('fr-FR') : ''; }

function UnmatchedRow({ item, onAssigned }) {
  const [search, setSearch]     = useState(item.extracted_client_name || '');
  const [results, setResults]   = useState([]);
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError]       = useState('');

  const doSearch = async () => {
    if (!search.trim()) return;
    setSearching(true); setError('');
    try {
      const r = await api.customers.list(item.company_id, search.trim(), { limit: 8, status: 'all' });
      setResults(Array.isArray(r) ? r : (r?.data || []));
    } catch (e) { setError(e.message || 'Erreur de recherche'); }
    finally { setSearching(false); }
  };

  const assign = async (customer) => {
    setAssigning(true); setError('');
    try {
      const cats = await api.ged.categories();
      const list = Array.isArray(cats) ? cats : (cats?.data || []);
      const found = list.find(c => c.slug === 'facture-archivee');

      await api.ged.createDocument({
        title: `Facture ${item.invoice_ref || '?'} — ${customer.name}`,
        reference: item.invoice_ref || null,
        category_id: found ? found.id : null,
        document_type: 'facture_archive',
        linked_entity_type: 'client',
        linked_entity_id: customer.id,
        linked_entity_name: customer.name,
        stored_name: item.stored_name,
        file_name: item.file_name,
        file_size: item.file_size,
        file_type: item.file_type,
        document_date: item.invoice_date || null,
        contract_amount: item.amount_ttc != null ? item.amount_ttc : null,
        status: 'archivé',
      });
      onAssigned();
    } catch (e) { setError(e.message || 'Erreur lors du rattachement'); }
    finally { setAssigning(false); }
  };

  return (
    <div style={{ padding:'10px 12px', background:'#fff7ed', border:'1px solid #ba751740', borderRadius:8, marginBottom:8 }}>
      <div style={{ fontSize:12.5, fontWeight:700 }}>
        {item.invoice_ref || 'Facture sans numéro'} — {fmtEUR(item.amount_ttc)}
        {item.invoice_date ? ` · ${fmtDate(item.invoice_date)}` : ''}
      </div>
      <div style={{ fontSize:11, color:'#ba7517', marginBottom:6 }}>
        Client détecté « {item.extracted_client_name || '?'} » — aucune correspondance automatique trouvée
      </div>
      <div style={{ display:'flex', gap:6 }}>
        <input className="form-control" style={{ flex:1 }} value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); doSearch(); } }}
          placeholder="Rechercher le bon client…" />
        <button type="button" className="btn sm" disabled={searching} onClick={doSearch}>{searching ? '…' : '🔍'}</button>
      </div>
      {error && <div style={{ fontSize:11, color:'#e24b4a', marginTop:4 }}>{error}</div>}
      {results.length > 0 && (
        <div style={{ marginTop:6, display:'flex', flexDirection:'column', gap:4 }}>
          {results.map(c => (
            <div key={c.id} onClick={() => !assigning && assign(c)}
              style={{ padding:'6px 8px', background:'#fff', border:'1px solid #e6e5e0', borderRadius:6, cursor:'pointer', fontSize:12 }}>
              <b>{c.name}</b> {c.city ? `— ${c.city}` : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ImportInvoiceArchive({ currentCompanyId, onClose }) {
  const [queue, setQueue]       = useState([]);
  const [created, setCreated]   = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [running, setRunning]   = useState(false);
  const [blocked, setBlocked]   = useState('');

  const handleFiles = (fileList) => {
    setQueue(Array.from(fileList).map(file => ({ file, status: 'pending' })));
    setCreated([]); setUnmatched([]);
  };

  // Envoi avec pause puis nouvel essai quand la limite d'envoi est atteinte
  // (429) : jusqu'à 3 essais, en attendant le délai indiqué par le serveur.
  const uploadWithRetry = async (file, i, q) => {
    for (let attempt = 1; ; attempt++) {
      try {
        return await api.ged.upload(file);
      } catch (e) {
        if (e.status !== 429 || attempt >= 3) throw e;
        const wait = Math.min(Math.max(e.retryAfter || 30, 5), 90);
        q[i] = { ...q[i], status: 'pause', wait }; setQueue([...q]);
        await new Promise(r => setTimeout(r, wait * 1000));
        q[i] = { ...q[i], status: 'upload' }; setQueue([...q]);
      }
    }
  };

  const runImport = async () => {
    setRunning(true); setBlocked('');
    const q = [...queue];
    const newCreated = [];
    const newUnmatched = [];

    for (let i = 0; i < q.length; i++) {
      if (q[i].status === 'done') continue;
      q[i] = { ...q[i], status: 'upload' }; setQueue([...q]);
      try {
        const up = await uploadWithRetry(q[i].file, i, q);
        q[i] = { ...q[i], status: 'extract' }; setQueue([...q]);
        const result = await api.invoiceArchive.extract(up);
        newCreated.push(...(result.created || []));
        newUnmatched.push(...(result.unmatched || []).map(u => ({ ...u, company_id: currentCompanyId })));
        q[i] = { ...q[i], status: 'done', createdCount: result.created?.length || 0, unmatchedCount: result.unmatched?.length || 0 };
      } catch (e) {
        // Module non inclus : inutile d'envoyer les suivants, on s'arrête
        // avec un message clair (les fichiers restants restent en attente).
        if (e.code === 'MODULE_NOT_ACTIVE') {
          q[i] = { ...q[i], status: 'pending' };
          setQueue([...q]);
          setBlocked("L'espace documentaire (GED) n'est pas inclus dans votre forfait : aucun fichier n'a été envoyé. Activez le module depuis « Mon abonnement », puis relancez l'import.");
          break;
        }
        q[i] = { ...q[i], status: 'error', error: e.message || 'Erreur' };
      }
      setQueue([...q]);
    }
    setCreated(c => [...c, ...newCreated]);
    setUnmatched(u => [...u, ...newUnmatched]);
    setRunning(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ width:760, maxWidth:'96vw', maxHeight:'88vh', display:'flex', flexDirection:'column' }}>
        <div className="modal-header">
          <h3 className="modal-title">📥 Import archives factures (Dolibarr)</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ overflowY:'auto', flex:1, padding:'0 4px' }}>
          <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:12 }}>
            Sélectionne un ou plusieurs PDF de factures exportées depuis Dolibarr. Chaque facture est archivée
            (aucune écriture comptable créée) et rattachée automatiquement au bon client quand c'est possible.
            Pense à sélectionner la bonne société active avant l'import.
          </div>

          <input type="file" accept="application/pdf" multiple onChange={e => handleFiles(e.target.files)} disabled={running} />

          {queue.length > 0 && (
            <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:6 }}>
              {queue.map((q, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'6px 10px', background:'#f9fafb', borderRadius:6 }}>
                  <span>{q.file.name}</span>
                  <span style={{ color: q.status === 'error' ? '#e24b4a' : q.status === 'done' ? '#1a9e72' : 'var(--text-muted)' }}>
                    {q.status === 'pending' && 'en attente'}
                    {q.status === 'upload' && 'envoi…'}
                    {q.status === 'pause' && `limite d'envoi atteinte — reprise dans ${q.wait} s…`}
                    {q.status === 'extract' && 'extraction…'}
                    {q.status === 'done' && `✓ ${q.createdCount} rattachée(s)${q.unmatchedCount ? `, ${q.unmatchedCount} à valider` : ''}`}
                    {q.status === 'error' && `✗ ${q.error}`}
                  </span>
                </div>
              ))}
            </div>
          )}

          {blocked && (
            <div style={{ marginTop:12, padding:'8px 12px', background:'#e24b4a14', color:'#e24b4a', borderRadius:8, fontSize:12.5 }}>
              {blocked}
            </div>
          )}

          {queue.length > 0 && (
            <button className="btn primary" style={{ marginTop:12 }} disabled={running} onClick={runImport}>
              {running
                ? `Import en cours… ${queue.filter(q => q.status === 'done' || q.status === 'error').length}/${queue.length}`
                : `Importer ${queue.filter(q => q.status !== 'done').length} fichier(s)`}
            </button>
          )}

          {created.length > 0 && (
            <div style={{ marginTop:20 }}>
              <div style={{ fontWeight:700, fontSize:12, color:'#1a9e72', marginBottom:8 }}>
                ✓ {created.length} facture(s) archivée(s) automatiquement
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:160, overflowY:'auto' }}>
                {created.map((c, i) => (
                  <div key={i} style={{ fontSize:11.5, padding:'6px 8px', background:'#f0fdf9', borderRadius:6 }}>
                    {c.invoice_ref || '?'} — {c.client_name} — {fmtEUR(c.amount_ttc)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {unmatched.length > 0 && (
            <div style={{ marginTop:20 }}>
              <div style={{ fontWeight:700, fontSize:12, color:'#ba7517', marginBottom:8 }}>
                ⚠️ {unmatched.length} facture(s) à rattacher manuellement
              </div>
              {unmatched.map((item, i) => (
                <UnmatchedRow key={i} item={item} onAssigned={() => setUnmatched(u => u.filter((_, idx) => idx !== i))} />
              ))}
            </div>
          )}
        </div>
        <div className="btn-group" style={{ justifyContent:'flex-end', marginTop:16 }}>
          <button className="btn" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
